import { useEffect, useState } from 'react';
import { verifyDiceRoll, verifyRoulettePocket, sha256Hex } from '@raketta/shared';
import { useAppStore } from '../store/useAppStore';
import { diceApi, PfActive, RotateResult } from '../api/dice';
import { Icon } from '../components/Icon';
import { formatRoll } from '../lib/format';
import { telegram } from '../lib/telegram';

interface VerifyForm {
  serverSeed: string;
  clientSeed: string;
  nonce: string;
  target: string;
}

export function FairnessScreen() {
  const setScreen = useAppStore((s) => s.setScreen);
  const lastGame = useAppStore((s) => s.lastGame);
  const verifyBetId = useAppStore((s) => s.verifyBetId);
  const showToast = useAppStore((s) => s.showToast);

  const [active, setActive] = useState<PfActive | null>(null);
  const [clientSeedInput, setClientSeedInput] = useState('');
  const [revealed, setRevealed] = useState<RotateResult['revealed'] | null>(null);

  const [betGame, setBetGame] = useState<'dice' | 'roulette'>('dice');
  const [form, setForm] = useState<VerifyForm>({ serverSeed: '', clientSeed: '', nonce: '0', target: '50' });
  const [verifyOut, setVerifyOut] = useState<{
    label: string;
    value: string;
    sub: string;
    isWin: boolean;
    commitmentOk: boolean | null;
  } | null>(null);
  const [expected, setExpected] = useState<number | null>(null);
  const [expectedHash, setExpectedHash] = useState<string | null>(null);
  const [match, setMatch] = useState<boolean | null>(null);

  const loadActive = () =>
    diceApi.pfActive().then((a) => {
      setActive(a);
      setClientSeedInput(a.clientSeed);
    });

  useEffect(() => {
    loadActive().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!verifyBetId) return;
    diceApi
      .bet(verifyBetId)
      .then((b) => {
        const game = b.game === 'roulette' ? 'roulette' : 'dice';
        setBetGame(game);
        setForm({
          serverSeed: b.serverSeed || '',
          clientSeed: b.clientSeed,
          nonce: String(b.nonce),
          target: game === 'dice' ? String(b.result.target) : '',
        });
        setExpected(game === 'dice' ? b.result.roll : b.result.pocket);
        setExpectedHash(b.serverSeedHash);
        setVerifyOut(null);
        if (!b.serverSeed) showToast('Server seed раскроется после ротации сида');
      })
      .catch(() => undefined);
  }, [verifyBetId]);

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} скопирован`);
    } catch {
      showToast('Не удалось скопировать', true);
    }
  };

  const saveClientSeed = async () => {
    const v = clientSeedInput.trim();
    if (!v) return showToast('Client seed пустой', true);
    try {
      const a = await diceApi.setClientSeed(v);
      setActive(a);
      showToast('Client seed обновлён');
      telegram.haptic('medium');
    } catch {
      showToast('Не удалось сохранить', true);
    }
  };

  const rotate = async () => {
    try {
      const r = await diceApi.rotate();
      setRevealed(r.revealed);
      await loadActive();
      showToast('Сид ротирован и раскрыт');
      telegram.notify('success');
    } catch {
      showToast('Не удалось ротировать', true);
    }
  };

  const runVerify = () => {
    const nonce = parseInt(form.nonce, 10);
    if (!form.serverSeed || !form.clientSeed || isNaN(nonce)) {
      return showToast('Заполни все поля', true);
    }
    const commitmentOk = expectedHash ? sha256Hex(form.serverSeed) === expectedHash : null;

    if (betGame === 'roulette') {
      const res = verifyRoulettePocket({ serverSeed: form.serverSeed, clientSeed: form.clientSeed, nonce });
      const match = expected != null ? res.pocket === expected : null;
      setVerifyOut({
        label: 'pocket',
        value: String(res.pocket),
        sub: res.color === 'red' ? 'Красное' : res.color === 'black' ? 'Чёрное' : 'Зеро',
        isWin: res.color !== 'black',
        commitmentOk,
      });
      setMatch(match);
    } else {
      const target = parseFloat(form.target);
      if (isNaN(target)) return showToast('Укажи «меньше чем»', true);
      const res = verifyDiceRoll({ serverSeed: form.serverSeed, clientSeed: form.clientSeed, nonce, target });
      const match = expected != null ? res.roll === expected : null;
      setVerifyOut({
        label: 'roll',
        value: res.roll.toFixed(2),
        sub: res.win ? 'Выигрыш' : 'Проигрыш',
        isWin: res.win,
        commitmentOk,
      });
      setMatch(match);
    }
  };

  return (
    <>
      <div className="page-head">
        <button className="icon-btn" onClick={() => setScreen(lastGame)} title="Назад">
          <Icon name="back" size={18} />
        </button>
        <span className="title">Честность</span>
      </div>

      {/* Активный сид */}
      <div className="card stack">
        <div className="section-title">Текущий сид (commitment)</div>
        <div>
          <div className="field-label">Хеш server seed · публикуется до игры</div>
          <div className="mono" onClick={() => active && copy(active.serverSeedHash, 'Хеш')}>
            {active ? active.serverSeedHash : '…'}
          </div>
        </div>
        <div className="row-between">
          <span className="muted">Текущий nonce</span>
          <span className="tnum" style={{ fontWeight: 700 }}>
            {active?.nonce ?? '—'}
          </span>
        </div>
        <div>
          <div className="field-label">Client seed (можно задать свой)</div>
          <input
            className="text-input"
            value={clientSeedInput}
            onChange={(e) => setClientSeedInput(e.target.value)}
            placeholder="свой client seed"
          />
        </div>
        <div className="row-between" style={{ gap: 8 }}>
          <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={saveClientSeed}>
            Сохранить
          </button>
          <button className="btn btn-sm" style={{ flex: 1 }} onClick={rotate}>
            Ротировать сид
          </button>
        </div>
        <p className="muted">
          Хеш фиксируется до ставок. После ротации старый server seed раскрывается — и любой бросок под ним
          можно пересчитать ниже.
        </p>
      </div>

      {/* Раскрытый сид */}
      {revealed && (
        <div className="card stack">
          <div className="section-title">Раскрытый server seed</div>
          <div className="mono" onClick={() => copy(revealed.serverSeed, 'Server seed')}>
            {revealed.serverSeed}
          </div>
          <div className="row-between">
            <span className="muted">Хеш совпадает</span>
            <span style={{ fontWeight: 700, color: 'var(--green)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {sha256Hex(revealed.serverSeed) === revealed.serverSeedHash ? (
                <>
                  <Icon name="check" size={15} /> да
                </>
              ) : (
                'нет'
              )}
            </span>
          </div>
        </div>
      )}

      {/* Верификатор */}
      <div className="card stack">
        <div className="section-title">Проверить результат</div>
        <p className="muted">
          Тот же код, что и на сервере ({betGame === 'roulette' ? 'verifyRoulettePocket' : 'verifyDiceRoll'}).
          Вставь сиды и получи тот же результат.
        </p>
        <div>
          <div className="field-label">Server seed (раскрытый)</div>
          <input
            className="text-input"
            value={form.serverSeed}
            onChange={(e) => setForm({ ...form, serverSeed: e.target.value })}
            placeholder="раскрытый server seed"
          />
        </div>
        <div>
          <div className="field-label">Client seed</div>
          <input
            className="text-input"
            value={form.clientSeed}
            onChange={(e) => setForm({ ...form, clientSeed: e.target.value })}
          />
        </div>
        <div className="row-between" style={{ gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div className="field-label">Nonce</div>
            <input
              className="text-input tnum"
              value={form.nonce}
              onChange={(e) => setForm({ ...form, nonce: e.target.value.replace(/[^0-9]/g, '') })}
            />
          </div>
          {betGame === 'dice' && (
            <div style={{ flex: 1 }}>
              <div className="field-label">Меньше чем</div>
              <input
                className="text-input tnum"
                value={form.target}
                onChange={(e) => setForm({ ...form, target: e.target.value.replace(/[^0-9.]/g, '') })}
              />
            </div>
          )}
        </div>
        <button className="btn btn-sm" onClick={runVerify}>
          Пересчитать
        </button>

        {verifyOut && (
          <div className={`verify-result ${match === false ? 'nomatch' : 'match'}`}>
            <div className="big" style={{ color: verifyOut.isWin ? 'var(--green)' : 'var(--red)' }}>
              {verifyOut.value}
            </div>
            <div style={{ fontSize: 13 }}>
              <div style={{ fontWeight: 700 }}>{verifyOut.sub}</div>
              {expected != null && (
                <div className="muted">
                  Сохранённый: {expected} · {match ? 'совпало' : 'не совпало'}
                </div>
              )}
              {verifyOut.commitmentOk != null && (
                <div className="muted">commitment: {verifyOut.commitmentOk ? 'подтверждён' : 'не сошёлся'}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
